import { map } from 'lodash-es'
import { useState } from 'react'

import { Button, Checkbox } from '@/components/ui'
import { Currency, Vouchers } from '../../../common/models'
import { IColumn, Table } from '../../../core/components/Table'
import {
  calcDaysBetweenTwoDates,
  formatDateTime,
} from '../../../core/constants/times'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import {
  downloadVoucherBatch,
  downloadVoucherSingle,
  getVoucherById,
} from '../../../core/services/voucher'
import { MessageBox } from '../messages/MessageBox'

export interface VoucherTableProps {
  setEditMode?: (edit: boolean) => void
  isCheck?: string[]
  setIsCheck?: (check: string[]) => void
  vouchers: Vouchers[]
  setVouchers: (v: Vouchers[]) => void
  setCurrentVoucher: (v: Vouchers) => void
  setShowCreateVoucherModal: (show: boolean) => void
  page: number
  setPage: (p: number) => void
  size: number
  setSize: (s: number) => void
  sortBy?: string
  setSortBy: (s?: string) => void
  sortDirection?: 'ASC' | 'DESC'
  setSortDirection: (s?: 'ASC' | 'DESC') => void
  totalElements: number
  setTotalElemenets: (t: number) => void
  isOneToOne: boolean
  dateFilters?: JSX.Element
  currency?: Currency | null
  handleSearchParams?: (col: IColumn, query: string | null) => void
}

export const VoucherTable = (props: VoucherTableProps): JSX.Element => {
  const {
    setEditMode,
    isCheck,
    setIsCheck,
    vouchers,
    setCurrentVoucher,
    setShowCreateVoucherModal,
    size,
    page,
    setPage,
    setSize,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    totalElements,
    dateFilters,
    currency,
    handleSearchParams,
  } = props

  const [isCheckAll, setIsCheckAll] = useState(false)

  const { hasEditPermission } = useCheckPermission({ page: 'VOUCHERS' })

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const handleSelectAll = () => {
    setIsCheckAll(!isCheckAll)
    setIsCheck?.(vouchers.map((voucher) => voucher.id))
    if (isCheckAll) {
      setIsCheck?.([])
    }
  }
  const handleClick = (id: string, checked: boolean) => {
    if (isCheck) {
      setIsCheck?.([...isCheck, id])
    }
    if (!checked && isCheck) {
      setIsCheck?.(isCheck.filter((item) => item !== id))
    }
  }
  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'DESC') {
        setSortDirection('ASC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('DESC')
      setSortBy(sort)
    }
  }

  const pageChange = async (newPage: number) => {
    setPage(newPage)
  }

  const downloadVoucher = async (id: string, type: 'PNG' | 'PDF' | 'SVG') => {
    if (!hasEditPermission) {
      return
    }
    try {
      const response = await downloadVoucherSingle(id, type)
      const href = URL.createObjectURL(response.data)

      // create "a" HTML element with href to file & click
      const link = document.createElement('a')
      link.href = href
      link.setAttribute('download', `${id}.${type}`) //or any other extension
      document.body.appendChild(link)
      link.click()

      // clean up "a" element & remove ObjectURL
      document.body.removeChild(link)
      URL.revokeObjectURL(href)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  const downloadBatch = async (id: string, type: 'PNG' | 'PDF' | 'SVG') => {
    if (!hasEditPermission) {
      return
    }
    try {
      const response = await downloadVoucherBatch(id, type)
      const href = URL.createObjectURL(response.data)

      // create "a" HTML element with href to file & click
      const link = document.createElement('a')
      link.href = href
      link.setAttribute('download', `${id}.${type}`) //or any other extension
      document.body.appendChild(link)
      link.click()

      // clean up "a" element & remove ObjectURL
      document.body.removeChild(link)
      URL.revokeObjectURL(href)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const calcultateExpiringIn = (row: any) => {
    const publishOrNow =
      row.publishDate > new Date().valueOf()
        ? row.publishDate
        : new Date().valueOf()
    const difference = calcDaysBetweenTwoDates(row.expiryDate, publishOrNow)

    return difference > 0 ? `${difference} Day` : 'Expired'
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox
          onCheckedChange={handleSelectAll}
          checked={isCheckAll}
          disabled={!hasEditPermission}
        />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck?.includes(row.id)}
          disabled={!hasEditPermission}
        />
      ),

      width: '16px',
    },
    {
      title: 'Id',
      accessor: 'id',
      sortable: true,
      render: (row) => row.id.substring(0, 8),
    },
    {
      title: 'Voucher Name',
      accessor: 'name',
      sortable: true,
      render: (row) => row.batchLabel,
    },
    {
      title: 'Voucher Code',
      accessor: 'voucherCode',
      searchable: true,
      render: (row) => (
        <div className="flex flex-col">
          <span>{row.code}</span>
          <div className="flex items-center gap-2">
            <Button
              variant="link"
              className="underline text-accent-1 p-0"
              size="sm"
              disabled={!hasEditPermission}
              onClick={() =>
                row.batchId
                  ? downloadBatch(row.batchId, 'PDF')
                  : downloadVoucher(row.id, 'PDF')
              }
            >
              PDF
            </Button>
            <span>|</span>
            <Button
              size="sm"
              variant="link"
              className="underline text-accent-1 p-0"
              disabled={!hasEditPermission}
              onClick={() =>
                row.batchId
                  ? downloadBatch(row.batchId, 'PNG')
                  : downloadVoucher(row.id, 'PNG')
              }
            >
              PNG
            </Button>
            <span>|</span>
            <Button
              size="sm"
              variant="link"
              className="underline text-accent-1 p-0"
              disabled={!hasEditPermission}
              onClick={() =>
                row.batchId
                  ? downloadBatch(row.batchId, 'SVG')
                  : downloadVoucher(row.id, 'SVG')
              }
            >
              SVG
            </Button>
          </div>
        </div>
      ),
    },
    {
      title: 'Discount',
      accessor: 'discount',
      render: (row) =>
        row.discountType === 'AMOUNT'
          ? `${row.amount} ${currency?.symbol}`
          : `${row.amount}%`,
    },
    {
      title: 'Tickets',
      accessor: 'tickets',
      render: (row) => (
        <div className="flex flex-col">
          <div className="flex flex-col">
            {map(row.tickets, (ticket, index) => {
              return (
                <div
                  key={`${ticket?.category?.name}-${ticket?.ticket?.name}-${index}`}
                  className="flex items-center gap-2"
                >
                  <Checkbox checked disabled />
                  <span>{`${ticket?.category?.name}, ${ticket?.ticket?.name}`}</span>
                </div>
              )
            })}
          </div>
        </div>
      ),
    },
    {
      title: 'Applied on Tickets',
      accessor: 'appliedOnTickets',
    },
    {
      title: 'Expiration Date',
      accessor: 'expirationDate',
      render: (row) => formatDateTime(row.expiryDate),
    },
    {
      title: 'Expiring in',
      accessor: 'expiringIn',
      render: (row) => <>{calcultateExpiringIn(row)}</>,
    },
    {
      title: 'Created by',
      accessor: 'createdBy',
      render: (row) => (
        <>{`${row.user ? row.user?.firstName : ''} ${
          row.user ? row.user?.lastName : ''
        }`}</>
      ),
    },
    {
      title: 'Uses',
      accessor: 'uses',
      render: (row) => (
        <>{`${row.usages} / ${
          row.unlimited ? 'unlimited' : row.voucherLimit
        }`}</>
      ),
    },
    {
      title: 'Status',
      accessor: 'status',
      render: (row) => (
        <>
          {row.status === 'ACTIVE' ? (
            <span style={{ color: '#198754' }}>Active</span>
          ) : (
            <span style={{ color: '#DC3545' }}>Inactive</span>
          )}
        </>
      ),
    },
    {
      title: 'View',
      accessor: '',
      render: (row) => (
        <>
          <Button
            variant="link"
            size="sm"
            className="underline text-accent-1 p-0"
            onClick={async () => {
              if (!hasEditPermission) {
                return
              }
              setShowCreateVoucherModal(true)
              setEditMode?.(true)
              const response = await getVoucherById(row.id)
              setCurrentVoucher(response.data)
            }}
          >
            View
          </Button>
        </>
      ),
    },
  ]

  return (
    <>
      {dateFilters}
      <Table
        isCheck={isCheck}
        columns={columns}
        handleSortBy={handleSortBy}
        data={vouchers}
        className="text-nowrap [&_th]:!whitespace-nowrap"
        handleSearch={handleSearchParams}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
